import { HttpStatusCode } from "axios";
import { ContactDetails, DeliveryDetails, OrderItem, ShippingDetails, OrderDetails, OrderCosting } from "../../models/DB";
import { ApiEndpoints } from "../../config/constants";
import uuid from "react-native-uuid";
import { getOrderPaymentDetails } from "../../utils/customFunctions";
import apiClient from "../api/ApiClient";
import { ResponseModel } from "../../models/Response";

const OrdersService = () => {

	const findOrderById = async (id: string): Promise<OrderDetails | null> => {
        try {
            const response = await apiClient.getPublic<ResponseModel<OrderDetails>>(ApiEndpoints.Orders.GetById(id));
            const { data, statusCode, errorMessage } = response.data;

            if (statusCode === HttpStatusCode.Ok) {
                // Ensure the data is cast to OrderDetails
                return data as OrderDetails;
            }

            console.log("Error fetching order by id: ", errorMessage);
            return null;
        } catch (error) {
            console.error("Error fetching order by id:", error);
            return null;
        }
    };

    const createOrder = async (
		cartItems: OrderItem[], 
		contactDetails: ContactDetails, 
		shippingDetails: ShippingDetails & DeliveryDetails,
		email: string
	) => {
        try {
            // Generate a unique invoice ID
            const invoiceId = uuid.v4().toString();
            
            // Get the order costing details
            const { baseCost, vat, totalCost } = getOrderPaymentDetails(cartItems, shippingDetails.deliveryFee);
            const orderCosting: OrderCosting = {
                baseCost,
                vat,
                totalCost,
            };

            // Get the delivery details
            const deliveryDetails: DeliveryDetails = {
                deliveryType: shippingDetails.deliveryType,
                deliveryFee: shippingDetails.deliveryFee,
                suburb: shippingDetails.suburb,
            };

            // Remove delivery details from shipping details
            const { deliveryType, deliveryFee, suburb, ...shippingDetailsWithoutDelivery } = shippingDetails;

            // Create the order data of type OrderDetails
            const createOrderDto: OrderDetails = {
                reference: invoiceId,
                order: cartItems,
                shippingDetails: shippingDetailsWithoutDelivery,
                contactDetails,
                email,
                ...deliveryDetails,
                ...orderCosting,
            };

            // Create the order
            const response = await apiClient.postPublic<ResponseModel<string>>(ApiEndpoints.Orders.Create, createOrderDto);
            const { statusCode, errorMessage, data } = response.data;

            if (statusCode === HttpStatusCode.Ok) {
                return data;
            } else {
                console.log("Error creating order:", errorMessage);
                return null;
            }
        } catch (error) {
            console.error("Error creating order:", error);
            return null;
        }
    };

    const getMyOrders = async (): Promise<OrderDetails[]> => {
        try {
            const response = await apiClient.get<ResponseModel<OrderDetails[]>>(ApiEndpoints.Orders.MyOrders);
            const { data, statusCode } = response.data;

            if (statusCode === HttpStatusCode.Ok && data) {
                return data;
            }

            return [];
        } catch (error) {
            console.error("Error fetching my orders:", error);
            return [];
        }
    };

    const cancelOrder = async (orderId: string): Promise<boolean> => {
        try {
            const response = await apiClient.put<ResponseModel<OrderDetails>>(ApiEndpoints.Orders.Cancel(orderId));
            return response.data.statusCode === HttpStatusCode.Ok;
        } catch (error) {
            console.error("Error cancelling order:", error);
            return false;
        }
    };

    const requestRefund = async (
        orderId: string,
        reason: string,
        customerNote?: string,
        amount?: number,
    ): Promise<{ success: boolean; message?: string }> => {
        try {
            const response = await apiClient.post<ResponseModel<any>>(ApiEndpoints.Refunds.Request, {
                orderId,
                reason,
                customerNote,
                amount,
            });

            if (response.data.statusCode === HttpStatusCode.Created || response.data.statusCode === HttpStatusCode.Ok) {
                return { success: true };
            }

            return { success: false, message: response.data.errorMessage || "Failed to submit refund request" };
        } catch (error: any) {
            return { success: false, message: error.response?.data?.errorMessage || "Failed to submit refund request" };
        }
    };

    return { createOrder, findOrderById, getMyOrders, cancelOrder, requestRefund };
};

export default OrdersService;
